import { useState, useEffect, useRef } from "react"
import { Link, useNavigate, useRouterState } from "@tanstack/react-router"
import { CardDialog } from "./card-dialog"

import { authClient } from "../lib/auth-client"
import { fetcher } from "../lib/api"
import { Button } from "./ui"
import { useTheme } from "./theme"
import { LogOut, ChevronLeft, Sun, Moon, Search, MessageCircleQuestion, CheckCircle2, X } from "lucide-react"
import { NotificationBell } from "./notification-bell"
import { useNotificationSocket } from "../hooks/useNotificationSocket"

/** Global search bar that lets you find cards across all your workspaces. Debounces input and shows a dropdown of results. */
function GlobalSearch({ onOpenCard }: { onOpenCard: (cardId: string, boardId: string, workspaceId: string) => void }) {
  const [q, setQ] = useState("")
  const [results, setResults] = useState<any[]>([])
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return }
    const t = setTimeout(() => {
      fetcher<any[]>(`/api/cards/search?q=${encodeURIComponent(q)}`).then((r) => {
        setResults(r)
        setOpen(true)
      }).catch(() => {})
    }, 300)
    return () => clearTimeout(t)
  }, [q])

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const go = (card: any) => {
    const ws = card.board?.project?.workspace?.id
    const brd = card.board?.id
    if (ws && brd) {
      onOpenCard(card.id, brd, ws)
    }
    setQ(""); setOpen(false)
  }

  return (
    <div ref={ref} className="relative hidden sm:block">
      <div className="flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-sm text-muted-foreground">
        <Search className="h-3.5 w-3.5" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search cards…  /"
          data-global-search
          className="w-40 bg-transparent text-sm focus:outline-none focus:w-56 transition-all"
          onFocus={() => results.length > 0 && setOpen(true)}
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute left-0 top-full z-50 mt-1 w-80 overflow-hidden rounded-xl border border-border bg-card shadow-xl">
          {results.map((card) => (
            <button
              key={card.id}
              onClick={() => go(card)}
              className="flex w-full flex-col px-4 py-2.5 text-left hover:bg-muted"
            >
              <span className="truncate text-sm font-medium">{card.title}</span>
              <span className="text-xs text-muted-foreground">
                {card.board?.project?.workspace?.name} / {card.board?.project?.name} / {card.board?.name}
              </span>
            </button>
          ))}
        </div>
      )}
      {open && q.length >= 2 && results.length === 0 && (
        <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-xl border border-border bg-card px-4 py-3 text-sm text-muted-foreground shadow-xl">
          No cards found
        </div>
      )}
    </div>
  )
}

const SUPPORT_CATEGORIES = [
  { value: "account", label: "Account & Login" },
  { value: "billing", label: "Billing & Plans" },
  { value: "bug", label: "Bug Report" },
  { value: "feature", label: "Feature Request" },
  { value: "other", label: "Other" },
]

function SupportModal({ session, onClose }: { session: any; onClose: () => void }) {
  const [name, setName] = useState(session?.user.name ?? "")
  const [email, setEmail] = useState(session?.user.email ?? "")
  const [category, setCategory] = useState("other")
  const [message, setMessage] = useState("")
  const [hp, setHp] = useState("")
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle")
  const [error, setError] = useState("")

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose() }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose])

  const submit = async () => {
    if (!name.trim() || !email.trim() || !message.trim()) { setError("Please fill in all fields."); return }
    setStatus("sending"); setError("")
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name, email, message, _hp: hp,
          category: SUPPORT_CATEGORIES.find(c => c.value === category)?.label ?? category,
          userId: session?.user.id,
          userEmail: session?.user.email,
        }),
      })
      if (!res.ok) { const d = await res.json() as { error: string }; throw new Error(d.error) }
      setStatus("sent")
    } catch (e: any) {
      setError(e.message || "Something went wrong."); setStatus("error")
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-end p-4 sm:items-center sm:justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-border bg-background shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <MessageCircleQuestion className="h-4 w-4 text-primary" />
            <h2 className="text-base font-semibold">Contact Support</h2>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-5">
          {status === "sent" ? (
            <div className="flex flex-col items-center gap-3 py-6 text-center">
              <CheckCircle2 className="h-10 w-10 text-primary" />
              <p className="font-semibold">Message sent!</p>
              <p className="text-sm text-muted-foreground">We'll get back to you soon.</p>
              <button onClick={onClose} className="mt-2 text-sm text-primary hover:underline">Close</button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium">Name</label>
                  <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="flex h-10 w-full rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium">Email</label>
                  <input value={email} onChange={e => setEmail(e.target.value)} placeholder="email" type="email" className="flex h-10 w-full rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50" />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Category</label>
                <select value={category} onChange={e => setCategory(e.target.value)} className="flex h-10 w-full rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50">
                  {SUPPORT_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Message</label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Describe your issue..." rows={4} className="flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none" />
              </div>
              {/* honeypot — hidden from real users, bots fill it in */}
              <input type="text" name="_hp" value={hp} onChange={e => setHp(e.target.value)} tabIndex={-1} aria-hidden="true" className="absolute -left-[9999px] opacity-0" />
              {error && <p className="text-sm text-destructive">{error}</p>}
              <div className="flex items-center justify-between gap-3">
                <a href="/support" className="text-sm text-muted-foreground hover:text-foreground">Full support page →</a>
                <button
                  onClick={submit}
                  disabled={status === "sending"}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60 transition-colors"
                >
                  {status === "sending" ? "Sending…" : "Send"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/** The main app shell. Renders the header with logo, back link, search, notifications, theme toggle, and user menu. Also handles the board color strip and tab bar if provided. */
export function Shell({
  title,
  back,
  actions,
  center,
  tabs,
  boardColor,
  children,
}: {
  title: React.ReactNode
  back?: { to: string; label: string }
  actions?: React.ReactNode
  center?: React.ReactNode
  tabs?: React.ReactNode
  boardColor?: string | null
  children: React.ReactNode
}) {
  const navigate = useNavigate()
  const { data: session } = authClient.useSession()
  const { theme, toggle } = useTheme()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [searchCard, setSearchCard] = useState<{ cardId: string; boardId: string; workspaceId: string } | null>(null)
  const [supportOpen, setSupportOpen] = useState(false)
  useNotificationSocket(!!session)

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="sticky top-0 z-50 flex items-center justify-between border-b bg-card/80 px-4 py-3 shadow-sm backdrop-blur sm:px-6">
        <div className="flex min-w-0 items-center gap-2 sm:gap-3">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <img src="/logo.svg" alt="Taskoru" className="h-9 w-9 rounded-xl shadow-sm" />
            <span className="hidden text-lg font-semibold tracking-tight sm:inline">Taskoru</span>
          </Link>
          {back && (
            <>
              <span className="text-muted-foreground">/</span>
              <Link
                to={back.to}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{back.label}</span>
              </Link>
            </>
          )}
          <span className="text-muted-foreground">/</span>
          <h1 className="truncate text-base font-semibold sm:text-lg">{title}</h1>
        </div>
        <div className="flex flex-1 items-center justify-center gap-3 px-4">
          <GlobalSearch onOpenCard={(cardId, boardId, workspaceId) => setSearchCard({ cardId, boardId, workspaceId })} />
          {center}
        </div>
        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          {actions}
          <Button variant="ghost" size="sm" onClick={() => setSupportOpen(true)} title="Contact support">
            <MessageCircleQuestion className="h-4 w-4" />
          </Button>
          <NotificationBell />
          <Button variant="ghost" size="sm" onClick={toggle} title={theme === "dark" ? "Light mode" : "Dark mode"}>
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Link to="/my-tasks" className="hidden text-sm text-muted-foreground hover:text-foreground md:inline">
            My Tasks
          </Link>
          <Link to="/profile" className="hidden max-w-[12rem] truncate text-sm text-muted-foreground hover:text-foreground md:inline">
            {session?.user.email}
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => authClient.signOut().then(() => navigate({ to: "/login" }))}
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>
      {tabs && <div className="border-b bg-card px-4 py-2 sm:px-6">{tabs}</div>}
      {boardColor && <div className="h-[3px] w-full" style={{ background: boardColor }} />}
      <div key={pathname} className="animate-fade-in flex flex-1 min-h-0 flex-col">
        {children}
      </div>
      {searchCard && (
        <CardDialog
          open={!!searchCard}
          onClose={() => setSearchCard(null)}
          cardId={searchCard.cardId}
          boardId={searchCard.boardId}
          workspaceId={searchCard.workspaceId}
        />
      )}
      {supportOpen && <SupportModal session={session} onClose={() => setSupportOpen(false)} />}
    </div>
  )
}
